import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { formatEnumLabel } from '../constants/enums';
import BusinessProcessUseCaseDialog, {
  emptyBusinessProcessRow,
} from './BusinessProcessUseCaseDialog';

export { emptyBusinessProcessRow };

/**
 * Business process use-case register: table + add/edit dialog.
 */
export default function BusinessProcessUseCaseTable({
  rows = [],
  onChange,
  processOptions = [],
  onRefreshLookups,
  error,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const primary = theme.palette.primary.main;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);

  const usedIds = useMemo(
    () => new Set(rows.map((r) => Number(r.businessProcessId)).filter(Boolean)),
    [rows]
  );

  const processName = (id) =>
    processOptions.find((p) => String(p.id) === String(id))?.name || '—';

  const openAdd = () => {
    setEditingRow(emptyBusinessProcessRow());
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditingRow({ ...emptyBusinessProcessRow(), ...row });
    setDialogOpen(true);
  };

  const handleSave = (row) => {
    const list = [...(rows || [])];
    const idx = list.findIndex((r) => r.key === row.key);
    if (idx >= 0) {
      list[idx] = row;
    } else {
      list.push(row);
    }
    onChange(list);
    setDialogOpen(false);
    setEditingRow(null);
    onRefreshLookups?.();
  };

  const confirmRemove = () => {
    if (!removeTarget?.key) return;
    onChange((rows || []).filter((r) => r.key !== removeTarget.key));
    setRemoveTarget(null);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" mb={1}>
        <Button
          size="small"
          variant="contained"
          color="secondary"
          startIcon={<AddIcon />}
          onClick={openAdd}
        >
          Add business process
        </Button>
      </Box>

      {error ? (
        <Typography variant="caption" color="error" display="block" mb={1}>
          {error}
        </Typography>
      ) : null}

      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          borderRadius: 2,
          borderColor: alpha(primary, isDark ? 0.35 : 0.14),
          bgcolor: isDark ? 'background.paper' : alpha(primary, 0.02),
          overflowX: 'auto',
        }}
      >
        <Table size="small" sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow
              sx={{
                bgcolor: alpha(primary, isDark ? 0.2 : 0.06),
                '& th': { fontWeight: 700, whiteSpace: 'nowrap' },
              }}
            >
              <TableCell>Business process</TableCell>
              <TableCell>Business owner</TableCell>
              <TableCell>Decision type</TableCell>
              <TableCell>Human oversight</TableCell>
              <TableCell>Oversight explanation</TableCell>
              <TableCell align="right" width={96}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(rows || []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No business processes yet. Click Add business process to register one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.key} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {processName(row.businessProcessId)}
                    </Typography>
                  </TableCell>
                  <TableCell>{row.businessOwner || '—'}</TableCell>
                  <TableCell>
                    {row.decisionType ? (
                      <Chip size="small" label={formatEnumLabel(row.decisionType)} variant="outlined" />
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>{row.humanOversight ? 'Yes' : 'No'}</TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        maxWidth: 280,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.humanOversight ? row.humanOversightExplanation || '—' : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(row)}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove">
                      <IconButton size="small" color="error" onClick={() => setRemoveTarget(row)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <BusinessProcessUseCaseDialog
        open={dialogOpen}
        initialValues={editingRow}
        onClose={() => {
          setDialogOpen(false);
          setEditingRow(null);
        }}
        onSave={handleSave}
        processOptions={processOptions}
        usedProcessIds={usedIds}
      />

      <Dialog
        open={Boolean(removeTarget)}
        onClose={() => setRemoveTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete business process?</DialogTitle>
        <DialogContent>
          <Typography>Do you want to delete the business process?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRemoveTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmRemove}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
